import ImageUploader from "components/shared/ImageUploder/ImageUploder";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import FieldSection from "components/shared/Tab/FieldSection";
import { getImage, updateTrips } from "services/createTripService";
import { toast } from "react-toastify";
import {
  uploadMultipleImages,
  uploadSingleImage,
} from "store/thunks/imageThunks";
import { useDispatch } from "react-redux";
import IconTextButton from "components/shared/buttons/IconTextButton";
import { ImageModal } from "components/index";
import { TripCoverImage, TripImage } from "assets/index";
import { getS3FileUrl } from "utils/index";
interface Image {
  file?: Blob | MediaSource;
  preview: string;
  fileName?: string;
  mimetype?: string;
}

export interface imageData {
  cover: {
    fileName: string;
    preview: string;
    mimetype?: string;
  };
  tripImages: {
    fileName: string;
    preview: string;
    mimetype?: string;
  }[];
}

interface ImageSectionProps {
  slots?: number | undefined;
  imageData: imageData | null;
  folderName: string;
  tripId: string;
  editable?: boolean;
  refetch: () => void;
  readOnly?: boolean;
}

const ImageSection = ({
  slots,
  imageData,
  folderName,
  tripId,
  editable,
  refetch,
  readOnly = false,
}: ImageSectionProps) => {
  const { cover, tripImages } = imageData ?? { cover: null, tripImages: [] };
  const dispatch = useDispatch();
  const [images, setImages] = useState<Image[]>(tripImages || []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [coverImage, setCoverImage] = useState<Image | null>(cover || null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showExampleImage, setShowExampleImage] = useState(TripCoverImage);

  const handleTripUpdate = async (data: any) => {
    const tripData: any = {
      images: data,
    };

    if (
      slots &&
      slots > 0 &&
      imageData?.cover?.fileName &&
      imageData?.tripImages?.length > 0
    ) {
      tripData.tripApprovalStatus = "In-Review";
      tripData.updatedAt= new Date().toISOString()
    }
    try {
      const response = await updateTrips(tripId, tripData);
      if (response.message === "Item updated successfully") {
        refetch();
      }
    } catch (error) {
      throw error;
    }
  };

  const handleCoverImageUpload = async () => {
    if (coverImage === null) {
      toast.error("Please upload a cover image first");
    }
    if (coverImage) {
      const data = {
        file: coverImage.file as File,
        folderName: folderName,
        fileName: "cover",
        mimetype: coverImage.mimetype,
      };

      const response = await dispatch(uploadSingleImage(data) as any);
      if (response.meta.requestStatus === "fulfilled") {
        toast.success("Cover image uploaded successfully");
        setCoverImage(response.payload);
        handleTripUpdate({
          cover: response.payload,
          tripImages: tripImages,
        });
      } else {
        toast.error("Failed to upload cover image");
        setCoverImage(null);
      }
    }
  };

  const handleImageUpload = async () => {
    if (images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }
    if (images.length < 3) {
      toast.error("Add at least 3 images");
      return;
    }
    const files = images.map((image) => ({
      file: image.file as File,
      folderName: folderName,
      fileName: image.fileName,
      mimetype: image.mimetype,
    })) as any;

    const response = await dispatch(uploadMultipleImages({ files }) as any);

    if (response.meta.requestStatus === "fulfilled") {
      toast.success("Trip image uploaded successfully");
      setImages(response.payload);

      handleTripUpdate({
        cover: {...coverImage, preview: getS3FileUrl(folderName,'cover')},
        tripImages: response.payload,
      });
    } else {
      toast.error("Failed to upload cover image");
      setImages([]);
    }
  };

  useEffect(() => {
    const convertFetchedImagesToFiles = async () => {
      if (!imageData) return;

      const { cover = null, tripImages = [] } = imageData as imageData;

      // Convert all trip images from URL to Blob (if they don't have `file`)
      const processedImages = await Promise.all(
        tripImages.map(async (img) => {
          if (img === null) return img; // Already has file, skip conversion

          const buffer = await getImage(folderName, img.fileName); // Fetch as blob

          return {
            ...img,
            file: buffer,
            fileName: img.fileName,
          };
        })
      );

      setImages(processedImages as any);

      // Convert cover image if available
      if (cover) {
        const buffer = await getImage(folderName, cover.fileName);

        setCoverImage({
          ...cover,
          file: buffer,
          mimetype: cover.mimetype,
        });
      }
    };

    convertFetchedImagesToFiles();
  }, []);

  const handleShowImage = (status: string) => {
    if (status === "trip") {
      setShowExampleImage(TripImage);
    } else if (status === "cover") {
      setShowExampleImage(TripCoverImage);
    }
    setShowImageModal(true);
  };

  return (
    <Box>
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
      >
        <Typography
          fontWeight={500}
          fontFamily={"Inter"}
          color="info"
          fontSize={14}
          mb={1}
          display={readOnly ? "none" : "block"}
          width={isMobile ? "100%" : "auto"}
        >
          *Please upload images in the sequence you want to be displayed in the
          app to the traveller.
        </Typography>

        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          gap={isMobile ? 1 : 2}
          width={isMobile ? "100%" : "auto"}
          marginLeft={isMobile ? 0 : "auto"}
        >
          <IconTextButton
            label="Sample Trip Image"
            sx={{ 
              marginRight: isMobile ? 0 : 2,
              width: isMobile ? "100%" : "auto"
            }}
            onClick={() => handleShowImage("trip")}
          />
          <IconTextButton
            label="Sample TripCover Image"
            sx={{ 
              width: isMobile ? "100%" : "auto"
            }}
            onClick={() => handleShowImage("cover")}
          />
        </Box>
      </Box>
      <FieldSection label="Cover Image" description={`**Images should be free of any messages, text, \n watermarks, company branding, or trip details.`}>
        <ImageUploader
          label="Upload Cover Image"
          images={coverImage}
          setImages={setCoverImage}
          maxFileSize={5 * 1024 * 1024} // 5MB
          maxFiles={1}
          handleUpload={handleCoverImageUpload}
          disabled={!editable}
          allowedAspectRatios={[9 / 16]}
          allowedRange={[0.56, 0.575]}
          readOnly={readOnly}
          renamedFileName="cover"
        />
      </FieldSection>

      <FieldSection label="Trip Image " description={`**Images should be free of any messages, text, \n watermarks, company branding, or trip details.`}>
        <ImageUploader
          label="Upload Trip Image"
          images={images}
          setImages={setImages}
          maxFileSize={5 * 1024 * 1024} // 5MB
          maxFiles={10}
          sequence={true}
          handleUpload={handleImageUpload}
          disabled={!editable}
          allowedAspectRatios={[16 / 9, 16 / 10]}
          allowedRange={[1.55, 1.8]}
          readOnly={readOnly}
        />
      </FieldSection>

      <ImageModal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
      >
        <img
          src={showExampleImage}
          alt="See Dummy Image"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
          }}
        />
      </ImageModal>
    </Box>
  );
};

export default ImageSection;